import useHeader from '@hooks/useHeader';
import { Trans, useTranslation } from 'react-i18next';
import EmailList from '../Components/EmailList';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { emailsScheduledQueryOptions } from '@utils/queryOptions';
import { useLoaderDeps, useNavigate } from '@tanstack/react-router';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import {
    CancelScheduleSendRounded,
    DeleteOutlineRounded,
    EventRepeatRounded,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import useDialog from '@hooks/useDialog';
import { useForm } from 'react-hook-form';
import DialogTextField from '@Components/General/dialog-content/text-field';
import useSimplePagination from '@hooks/useSimplePagination';
import useSimpleSort from '@hooks/useSimpleSort';
import SimpleSort from '@Components/Common/SimpleSort';
import SimplePagination from '@Components/Common/SimplePagination';
import Toolbar from '@Components/General/Toolbar';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import NoScheduledEmailsFound from './no-scheduled-found';
import NoScheduledEmails from './no-scheduled';
import { useSchesuledEmailOptions } from './useSchesuledEmailOptions';

const EmailsScheduled = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const searchParamsEmpty = useEmptySearchParams();

    const simplePageHook = useSimplePagination();

    const simpleSortHook = useSimpleSort({
        defaultSortField: 'schedule_time',
    });

    const headerOptions = useMemo(() => {
        return {
            title: t('Scheduled Emails'),
            fabAction: () => () => navigate({ to: '../drafts/new' }),
            searchProps: {
                searchPlacholder: t('search:emails-scheduled'),
            },
        };
    }, []);

    useHeader(headerOptions);

    const {
        data: { results, count },
    } = useSuspenseQuery(emailsScheduledQueryOptions(useLoaderDeps({})));

    const getScheduleEmailOptions = useSchesuledEmailOptions();

    const getQuickOptions = (item): QuickOption[] => {
        const { reschedule, unschedule, deleteEmail } =
            getScheduleEmailOptions(item);
        return [reschedule, unschedule, deleteEmail];
    };

    if (results.length === 0 && searchParamsEmpty) {
        return <NoScheduledEmails />;
    }

    return (
        <Box flexGrow={1}>
            <Box display="flex" flexDirection="column" height="100%">
                <Toolbar>
                    <SimpleSort
                        sortOptions={[
                            {
                                label: t('Scheduled Time'),
                                value: 'schedule_time',
                            },
                            { label: t('Subject'), value: 'subject' },
                        ]}
                        {...simpleSortHook}
                    />
                    <SimplePagination count={count} {...simplePageHook} />
                </Toolbar>
                {results.length === 0 ? (
                    <NoScheduledEmailsFound />
                ) : (
                    <EmailList
                        data={results}
                        metaText={item =>
                            `${t('Scheduled')}: ${item.schedule_time.toContextualDateTime()}`
                        }
                        action={item => (
                            <Box
                                onClick={e => e.preventDefault()}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <QuickOptions options={getQuickOptions(item)} />
                            </Box>
                        )}
                    />
                )}
            </Box>
        </Box>
    );
};

export default EmailsScheduled;
